import { all, call, getContext, put, select, take, takeEvery, delay } from 'redux-saga/effects';

import dynaForms from '@elab/core/utils/dynaForms';
import dynaFormsApi from '@elab/core/utils/dynaForms/api';
import { initAddressRelationsWatchers } from '@elab/core/utils/addressRelations/saga';
import {
    actions as globalActions,
    selectors as globalSelectors,
    types as globalTypes,
} from '@elab/core/store/global';
import {
    actions as addressActions,
    selectors as addressSelectors,
    types as addressesTypes,
} from '@elab/core/store/address';
import { createWatcher } from '@elab/core/store/core/saga';
import addressApi from '@elab/core/utils/address/api';
import { getSearchParam } from '@elab/core/utils/address';
import {
    defineAdditionalAddressFieldsDisplay,
    getConfirmationFromModal,
    initAddressSuggestionWatchers,
    setFakeValidationOnAddressTypeChange,
} from '@elab/core/utils/address/sagaHelpers';
import {
    addAddressFormSuccessMessage,
    addressPopupByStoreName,
    addressTypes,
    editAddressFormSuccessMessage,
} from '@elab/core/utils/constants';
import {
    actions as uiActions,
    selectors as uiSelectors,
    types as uiTypes,
} from '@elab/core/store/ui';
import { handleCloseDialog } from '@elab/core/utils/displayOneByOneHelper';
import * as configSelectors from '@elab/core/store/config/selectors';
import {
    actions as cartActions,
    api as cartApi,
    selectors as cartSelectors,
} from '@elab/core/store/cart';
import { selectors as userSelectors } from '@elab/core/store/user';
import isEmpty from '@elab/core/utils/isEmpty';
import { getValueByStorePath } from '@elab/core/utils/helpers';

import { getActions } from './actions';
import { getSelectors } from './selectors';
import { getTypes } from './types';

export const doSubmit = ({ actions, selectors, storeName }) =>
    function*(addressDto) {
        const addressPopup = addressPopupByStoreName[storeName];
        const isEditing = yield select(selectors.isEditing);
        const editingId = yield select(selectors.editingId);
        const submitURL = yield select(selectors.getSubmitUrl);
        const redirectURL = yield select(selectors.getSuccessRedirectLink);
        const isDialogOpen = yield select(uiSelectors.getDialogState, addressPopup);
        const addressType = yield select(selectors.addressType);
        const configAddressType = yield select(configSelectors.getSubmitAddressType);
        const closeDialogWhenModalOpens = yield select(
            configSelectors.getCloseDialogWhenModalOpens,
        );
        const openAddressBookAfterSubmit = yield select(
            configSelectors.getOpenAddressBookAfterSubmit,
        );
        const radioView = yield select(selectors.radioView);
        const submitEditUrl = yield select(selectors.submitEditUrl);
        const submitAddUrl = yield select(selectors.submitAddUrl);
        const submitEditMethod = yield select(selectors.submitEditMethod);
        const userId = yield select(userSelectors.getUserId);
        const cartId = yield select(cartSelectors.getCartId);
        const selectForPaymentAfterEditing = yield select(selectors.selectForPaymentAfterEditing);

        const isDelivery = addressType === 'shippingAddress';
        let method = isEditing ? submitEditMethod : 'post';

        const isEditingDeliveryAddressOnRadioView = radioView && isDelivery && isEditing;

        const editUrl =
            submitEditUrl && !isEditingDeliveryAddressOnRadioView
                ? submitEditUrl
                : `${submitURL}/${editingId}`;
        const addUrl = submitAddUrl || submitURL;
        let url = isEditing ? editUrl : addUrl;

        const addressTypes = ['shippingAddress', 'billingAddress'];

        if (addressType) {
            addressTypes.forEach(type => (addressDto[type] = addressType === type));
        } else if (configAddressType) {
            addressTypes.forEach(type => (addressDto[type] = configAddressType === type));
        }

        if (radioView && !isDelivery && isEditing) {
            addressDto.id = editingId;
        }

        const data = yield call(dynaFormsApi.doSubmit, url, addressDto, method);

        if (selectForPaymentAfterEditing && isEditing) {
            yield call(
                isDelivery ? cartApi.putDeliveryAddress : cartApi.putBillingAddress,
                userId,
                cartId,
                editingId,
            );
        }

        const message = isEditing ? editAddressFormSuccessMessage : addAddressFormSuccessMessage;
        yield put(globalActions.addMessage(message));

        if (isDialogOpen || (closeDialogWhenModalOpens && radioView)) {
            if (isDialogOpen) {
                yield put(uiActions.closeDialogByName(addressPopup));
            }

            if (addressType) {
                if (radioView) {
                    const selectedAddressId = editingId || data.id;

                    if (isDelivery) {
                        yield put(addressActions.setCurrentDeliveryAddressId(selectedAddressId));
                    } else {
                        yield put(addressActions.setCurrentBillingAddressId(selectedAddressId));
                    }
                }

                yield put(actions.setAddressType({ addressType: null }));
            }
            yield put(addressActions.loadAddresses());
            yield put(addressActions.loadBillingAddresses());

            if (addressType) {
                if (radioView) {
                    const selectedAddressId = editingId || data.id;
                    if (isDelivery) {
                        yield take(addressesTypes.SET_ADDRESSES);
                        const deliveryAddress = yield select(
                            addressSelectors.getAddressById,
                            selectedAddressId,
                        );
                        if (!isEmpty(deliveryAddress)) {
                            yield put(cartActions.setDeliveryAddress(deliveryAddress));
                        }
                    } else {
                        yield take(addressesTypes.SET_BILLING_ADDRESSES);
                        const billingAddress = yield select(
                            addressSelectors.getBillingAddressById,
                            selectedAddressId,
                        );
                        if (!isEmpty(billingAddress)) {
                            yield put(cartActions.setPaymentAddress(billingAddress));
                        }
                    }

                    if (openAddressBookAfterSubmit) {
                        yield put(
                            addressActions.openAddressBook({
                                name: isDelivery
                                    ? 'address-book-dialog-delivery'
                                    : 'address-book-dialog-billing',
                            }),
                        );
                    }
                }
            }
        } else {
            yield put(globalActions.redirect(redirectURL));
        }
    };

export const closePopup = ({ storeName }) =>
    function*() {
        const closeDialog = yield select(configSelectors.getCloseDialogWhenModalOpens);
        const addressPopup = addressPopupByStoreName[storeName];
        const isDialogOpen = yield select(uiSelectors.getDialogState, addressPopup);

        if (closeDialog && isDialogOpen) {
            yield put(uiActions.closeDialogByName(addressPopup));
        }
    };

export const prefillFormWithData = ({ storeName, actions, selectors }) =>
    function*(address) {
        const formFields = yield select(selectors.getData);
        const shouldCleanPhoneNumber = yield select(configSelectors.cleanPhoneNumber);
        const isMobileNumber = yield select(configSelectors.setMobileNumber);

        // On the frontend we use isocode as a value in form country dropdowns,
        // but from the back end we receive the country object as a current value
        // in order to form's logic work properly we need to replace country object with isocode property.
        if (address.country) {
            address.country = address.country.isocode;
        }

        // If we should clean phone number we still need show id with mask applied when prefill
        if (shouldCleanPhoneNumber && address.formattedPhone) {
            const fieldValue = isMobileNumber ? 'mobileNumber' : 'phone';
            address[fieldValue] = address.formattedPhone;
        }

        yield call(defineAdditionalAddressFieldsDisplay, {
            storeName,
            valuesSource: address,
        });
        yield all(
            Object.keys(formFields)
                .filter(field => address[field])
                .map(field => {
                    const value =
                        field === 'title' ? address.titleCode || address.title : address[field];
                    return put(actions.setDataFieldValue(field, value, true, true));
                }),
        );
        const preFillCompanyOptionsFormFields = yield select(
            configSelectors.preFillCompanyOptionsFormFields,
        );
        if (preFillCompanyOptionsFormFields && address.addressType === 'CORPORATE') {
            const companyOptionsFormFields = formFields.companyOptionsForm
                ? formFields.companyOptionsForm.fields
                : [];
            yield all(
                companyOptionsFormFields.map((field, index) => {
                    const value = address[field.name] || '';
                    const fieldName = `companyOptionsForm.fields.${index}`;
                    return put(actions.setDataFieldValue(fieldName, value, true, true));
                }),
            );
        }
    };

export const initializeAddressForm = ({ actions, types }) =>
    function*() {
        // before prefilling the metadata should be received
        yield take(types.SET_INITIAL_DATA);

        const id = yield call(getSearchParam, 'id');

        // means that user wants to update existing address, but not to create a new one
        if (id) {
            yield put(actions.setEditing({ id }));
        }
    };

export const setEditing = ctx =>
    function*({ payload: { id } }) {
        const address = yield call(addressApi.getAddressByID, id);
        if (typeof address !== 'undefined') {
            const prefill = yield call(prefillFormWithData, ctx);
            yield call(prefill, address);
        }
    };

export const openFormInDialog = ({ storeName, actions, selectors }) =>
    function*(action) {
        const {
            id,
            addressType,
            radioView,
            useCartUrlForEdit,
            useCartUrlForAdd,
            submitEditMethod,
            selectForPaymentAfterEditing,
        } = action.payload;
        const messages = yield select(globalSelectors.getMessages);
        const userId = yield select(userSelectors.getUserId);
        const cartId = yield select(cartSelectors.getCartId);
        const addressPopup = addressPopupByStoreName[storeName];
        const messageToRemove = messages.find(message => message.id === 'addressBookMessage');
        const isDelivery = addressType === 'shippingAddress';

        const cartSubmitURL = `${cartApi.getApiName(userId)}${cartApi.getCartApiName(
            userId,
            cartId,
        )}/addresses/${isDelivery ? 'delivery' : 'billing'}`;

        if (messageToRemove) {
            yield put(globalActions.removeMessage(messageToRemove));
        }
        yield put(actions.reset());
        yield put(actions.resetFormData());
        if (id) {
            yield put(actions.setEditing({ id }));
        }
        if (addressType) {
            yield put(actions.setAddressType({ addressType }));
        }
        if (radioView) {
            yield put(actions.setRadioView({ radioView }));
        }
        if (useCartUrlForEdit) {
            yield put(actions.setSubmitEditUrl({ submitEditUrl: cartSubmitURL }));
        }
        if (useCartUrlForAdd) {
            yield put(actions.setSubmitAddUrl({ submitAddUrl: cartSubmitURL }));
        }
        if (submitEditMethod) {
            yield put(actions.setSubmitEditMethod({ submitEditMethod }));
        }
        if (selectForPaymentAfterEditing) {
            yield put(actions.setSelectForPaymentAfterEditing({ selectForPaymentAfterEditing }));
        }
        const setFakeValidationAddressType = yield select(
            configSelectors.setFakeValidationAddressType,
        );
        if (setFakeValidationAddressType && !isDelivery) {
            yield call(setFakeValidationOnAddressTypeChange, selectors, actions);
        }
        yield put(uiActions.openDialog({ name: addressPopup }));
    };

export function* onSetDataFieldValue({ payload }) {
    const { actions, selectors } = yield getContext('ctx');
    const preFillCompanyOptionsFormFields = yield select(
        configSelectors.preFillCompanyOptionsFormFields,
    );
    if (preFillCompanyOptionsFormFields && payload.name === 'companyName') {
        if (payload.value !== null) {
            yield put(actions.setDataFieldValue('firstName', payload.value));
            yield put(actions.setDataFieldValue('lastName', payload.value));
        }
        return;
    }
    if (payload.name !== 'addressType') {
        return;
    }
    const setFakeValidationAddressType = yield select(configSelectors.setFakeValidationAddressType);
    if (setFakeValidationAddressType) {
        yield call(setFakeValidationOnAddressTypeChange, selectors, actions);
    }
    if (payload.value === addressTypes.INDIVIDUAL) {
        if (preFillCompanyOptionsFormFields) {
            const title = yield select(selectors.getDataFieldValue, 'title');
            yield put(actions.setDataFieldValue('companyName', null));
            if (title === 'N/A') yield put(actions.setDataFieldValue('title', null));
        }
        yield put(actions.setDataFieldValue('companyOptionsForm', null));
    } else {
        let companyOptionFormValue = {};
        if (preFillCompanyOptionsFormFields) {
            yield put(actions.setDataFieldValue('title', 'N/A'));
            yield delay(100);
            const companyOptionsForm = yield select(selectors.getDataField, 'companyOptionsForm');
            const companyOptionsFormFields = companyOptionsForm ? companyOptionsForm.fields : [];
            companyOptionsFormFields.forEach(field => {
                const { name, value } = field || {};
                companyOptionFormValue[name] = value;
            });
            yield put(actions.setDataFieldValue('title', 'N/A'));
            if (setFakeValidationAddressType) {
                const formMetaData = yield select(selectors.getMetadata);
                const companyNameValue = yield select(selectors.getDataFieldValue, 'companyName');
                const companyName = getValueByStorePath('0.fields.companyName', formMetaData) || {
                    constraints: [],
                };
                const payload = {
                    name: 'companyName',
                    validations: {
                        required: true,
                        constraints: [...companyName.constraints],
                        value: companyNameValue,
                    },
                };
                yield put(actions.setFakeValidation(payload));
            }
        }
        yield put(
            actions.addFieldItem({ name: 'companyOptionsForm', value: companyOptionFormValue }),
        );
    }
}

export const createSagaFor = storeName => formId => {
    const actions = getActions(storeName);
    const selectors = getSelectors(storeName);
    const types = getTypes(storeName);
    const ctx = { storeName, actions, selectors, types };

    return [
        ...dynaForms.createSagaFor(
            ctx,
            dynaFormsApi.getFormMetadata(formId || storeName),
            doSubmit(ctx),
            getConfirmationFromModal,
            true,
        ),
        createWatcher(globalTypes.RESET_FORMS_MESSAGES, actions.resetGlobalMessage()),
        takeEvery(uiTypes.OPEN_MODAL, closePopup(ctx)),
        takeEvery(types.INITIALIZE, initializeAddressForm(ctx)),
        takeEvery(types.SET_EDITING, setEditing(ctx)),
        takeEvery(types.OPEN_FORM_IN_DIALOG, openFormInDialog(ctx)),
        takeEvery(types.SET_DATA_FIELD_VALUE, onSetDataFieldValue),
        takeEvery(
            [uiTypes.CLOSE_DIALOG_BY_NAME, uiTypes.CLOSE_DIALOG],
            handleCloseDialog,
            storeName,
        ),
        initAddressSuggestionWatchers({ actions, selectors, types }),
        initAddressRelationsWatchers(ctx, storeName),
    ];
};
